<template>
  <div class="lead">
    <div class="lead__wrapper" :class="{'lead__wrapper--indent': !leadInfo.manager}">
      <template
        v-if="leadInfo.manager && (is_general_manager || is_sales_manager || is_sales_supervisor || is_support)"
      >
        <app-cells
          v-if="leadInfo.manager || (['deposit_paid', 'full_paid', 'pro_forma'].includes(leadInfo.status.code) && !leadInfo.client_group)"
        >
          <app-button @click.native="onShowDateModal">Add communication</app-button>
          <app-button @click.native="onOpenModalComment">Add comment</app-button>
          <app-button
            @click.native="onShowFollowModal"
            :disabled="!leadInfo.last_contact_expired || !leadInfo.email"
          >
            Send Follow up
          </app-button>
          <app-button
            v-if="['deposit_paid', 'full_paid', 'pro_forma'].includes(leadInfo.status.code) && !leadInfo.client_group"
            @click.native="$modal.show('client-form')"
          >
            Create group
          </app-button>
        </app-cells>
      </template>
      <div class="lead__part">
        <div class="lead__name">
          <img src="@/assets/img/personal-details.svg" alt="personal">
          <span>Lead info</span>
        </div>
        <div class="lead__row">
          <app-form-group label="Travel time">
            <app-date-picker
              @input="onCheckForm({ travel_time: form.travel_time })"
              v-model="form.travel_time"
              format="MMM YYYY"
              type="month"
              placeholder="Travel time"
            />
          </app-form-group>
          <app-form-group label="Activities" class="form-group--mh">
            <app-cells :indent="false">
              <app-checkbox-pic @change.native="onCheckForm({ climbing: form.climbing })"
                                v-model="form.climbing" activeColor="#219b52"
                                picPath="climbing-table-icon.svg"/>
              <app-checkbox-pic @change.native="onCheckForm({ safari: form.safari })"
                                v-model="form.safari" activeColor="#eb9514"
                                picPath="safari-table-icon.svg"/>
              <app-checkbox-pic @change.native="onCheckForm({ day_trip: form.day_trip })"
                                v-model="form.day_trip" activeColor="#77b017"
                                picPath="day_trips-table-icon.svg"/>
              <app-checkbox-pic @change.native="onCheckForm({ island: form.island })"
                                v-model="form.island" activeColor="#da3e63"
                                picPath="island-table-icon.svg"/>
            </app-cells>
          </app-form-group>
          <app-form-group label="Tour operator">
            <app-select
              @input="onCheckForm({ tour_operator: form.tour_operator })"
              v-model="form.tour_operator"
              :reduce="item => item.id"
              :options="tour_operators_options"
              placeholder="Tour operator"
            />
          </app-form-group>
          <app-form-group label="Next contact" v-if="leadInfo.status.next_contact">
            <app-date-picker
              @input="onCheckForm({ next_contact: form.next_contact })"
              v-model="form.next_contact"
              placeholder="Next contact"
              :error="$v.form.next_contact.$error"
            />
            <template #error>
              <div v-if="$v.form.next_contact.$dirty && !$v.form.next_contact.minValue">
                Date cannot be less than the current date
              </div>
            </template>
          </app-form-group>
          <app-form-group v-if="['deposit_paid', 'full_paid', 'pro_forma'].includes(leadInfo.status.code)"
                          label="Asana" label-for="asana">
            <app-input
              @change.native="onCheckForm({ asana: form.asana })"
              v-model="form.asana"
              :error="$v.form.asana.$error"
              id="asana"
              placeholder="Asana"
              name="asana"
            />
            <template #error>
              <div v-if="$v.form.asana.$invalid">Wrong link</div>
            </template>
          </app-form-group>
        </div>
      </div>
      <div class="lead__part">
        <div class="lead__name">
          <img src="@/assets/img/personal-details.svg" alt="personal">
          <span>Personal details</span>
        </div>
        <div class="lead__row lead__row--names">
          <app-form-group label="Name" label-for="lead_name" required>
            <app-input
              @change.native="onCheckForm({ name: form.name })"
              v-model="form.name"
              :error="$v.form.name.$error"
              id="lead_name"
              placeholder="Name"
              name="lead_name"
            />
            <template #error>
              <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group label="First name" label-for="lead_first_name">
            <app-input
              @change.native="onCheckForm({ first_name: form.first_name })"
              v-model="form.first_name"
              id="lead_first_name"
              placeholder="First name"
              name="lead_first_name"
            />
          </app-form-group>
          <app-form-group label="Last name" label-for="lead_last_name">
            <app-input
              @change.native="onCheckForm({ last_name: form.last_name })"
              v-model="form.last_name"
              id="lead_last_name"
              placeholder="Last name"
              name="lead_last_name"
            />
          </app-form-group>
        </div>
        <div class="lead__row lead__row--demographics">
          <app-form-group label="Pax" label-for="lead_pax">
            <app-input
              @change.native="onCheckForm({ pax: form.pax })"
              v-model.number="form.pax"
              type="number"
              min="0"
              id="lead_pax"
              placeholder="Pax"
              name="lead_pax"
            />
          </app-form-group>
          <app-form-group label="Country">
            <app-select
              @input="onCountryChange"
              v-model="form.country"
              :reduce="item => item.code"
              :options="country_options"
              placeholder="Country"
            />
            <template #additional>
              <span class="lead__source">{{ sourceLabel(leadInfo.country_source) }}</span>
            </template>
          </app-form-group>
          <app-form-group label="Time zone" :disabled="!form.country">
            <app-select
              @input="onTimezoneChange"
              v-model="form.country_timezone"
              :reduce="item => item.id"
              :options="timezone_options"
              :disabled="!form.country"
              :display-field="timezoneLabel"
              label="label"
              placeholder="Time zone"
            />
            <template #additional>
              <span class="lead__source">{{ timezoneSourceLabel }}</span>
            </template>
          </app-form-group>
          <app-form-group label="Language" required>
            <app-select
              @input="onCheckForm({ language: form.language })"
              v-model="form.language"
              :options="languages_options"
              :reduce="item => item.code"
              :clearable="false"
              placeholder="Country"
              :error="$v.form.language.$error"
            />
            <template #error>
              <div v-if="$v.form.language.$dirty && !$v.form.language.required">Required field</div>
            </template>
          </app-form-group>
        </div>
      </div>
      <div class="lead__part">
        <div class="lead__name">
          <img src="@/assets/img/contact-details.svg" alt="personal">
          <span>Contact details</span>
        </div>
        <div class="lead__row">
          <app-form-group label="E-mail" label-for="email">
            <app-input
              @change.native="onCheckForm({ email: form.email })"
              v-model="form.email"
              :error="$v.form.email.$error"
              id="email"
              type="email"
              placeholder="E-mail"
              name="email"
            />
            <template #additional>
              <button
                v-tooltip="{content: form.email, placement: 'top-end', offset: 10}"
                @click="onCopyContactLink(form.email)"
                class="table-default__activity table-default__activity--mail"
                :class="{'table-default__activity--active': form.email}"
              />
            </template>
            <template #error>
              <div v-if="$v.form.email.$dirty && !$v.form.email.email">Enter the correct e-mail format
              </div>
            </template>
          </app-form-group>
          <app-form-group label="Phone">
            <app-phone
              @change.native="onCheckForm({ phone: form.phone })"
              v-model="form.phone"
              placeholder="+255 786 350 350"
              :error="$v.form.phone.$dirty && form.phone === 0"
            />
            <template #additional>
              <button
                v-tooltip="{content: form.phone, placement: 'top-end', offset: 10}"
                @click="onCopyContactLink(form.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': form.phone}"
              />
              <a
                :href="`https://t.me/${form.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--tg"
                :class="{'table-default__activity--active': form.phone}"
              />
              <a
                :href="`https://wa.me/${form.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--wa"
                :class="{'table-default__activity--active': form.phone}"
              />
            </template>
            <template #error>
              <div v-if="$v.form.phone.$dirty && form.phone === 0">
                Invalid phone number format
              </div>
            </template>
          </app-form-group>
          <app-form-group label="Facebook" label-for="facebook">
            <app-input
              @change.native="onCheckForm({ facebook: form.facebook })"
              v-model="form.facebook"
              :error="$v.form.facebook.$error"
              id="facebook"
              placeholder="Facebook"
              name="facebook"
            />
            <template #additional>
              <a
                :href="form.facebook"
                target="_blank"
                class="table-default__activity table-default__activity--facebook"
                :class="{'table-default__activity--active': form.facebook}"
              />
            </template>
            <template #error>
              <div v-if="$v.form.facebook.$invalid">Wrong link</div>
            </template>
          </app-form-group>
          <app-form-group label="Instagram" label-for="instagram">
            <app-input
              @change.native="onCheckForm({ instagram: form.instagram })"
              v-model="form.instagram"
              :error="$v.form.instagram.$error"
              id="instagram"
              placeholder="Instagram"
              name="instagram"
            />
            <template #additional>
              <a
                :href="form.instagram"
                target="_blank"
                class="table-default__activity table-default__activity--instagram"
                :class="{'table-default__activity--active': form.instagram}"
              />
            </template>
            <template #error>
              <div v-if="$v.form.instagram.$invalid">Wrong link</div>
            </template>
          </app-form-group>
          <span></span>
          <app-form-group label="Source" required>
            <app-select
              @input="onCheckForm({ source: form.source, trengo_ticket: form.source === 'jivosite' || form.source === 'whatsapp' ? form.trengo_ticket?.id : null })"
              v-model="form.source"
              :options="source_options"
              :reduce="item => item.code"
              :clearable="false"
              placeholder="Source"
              :error="$v.form.source.$error"
            />
            <template #error>
              <div v-if="$v.form.source.$dirty && !$v.form.source.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group v-if="form.source && (form.source === 'jivosite' || form.source === 'whatsapp')" label="Trengo ticket">
            <app-select
              @input="onCheckForm({ trengo_ticket: form.trengo_ticket, source: form.source })"
              @search="onTicketSearch"
              v-model="form.trengo_ticket"
              :options="trengo_ticket_options"
              :reduce="item => item.id"
              :display-field="option => `Ticket #${option.ticket_id}`"
              label="ticket_id"
              placeholder="Trengo ticket"
            />
          </app-form-group>
        </div>
      </div>
      <div v-if="otherDetails.length" class="lead__part">
        <div class="lead__name">
          <img src="@/assets/img/other-details.svg" alt="other">
          <span>Other details</span>
        </div>
        <div class="lead__row">
          <app-form-group
            v-for="detail in otherDetails"
            :key="detail.label"
            :label="detail.label"
            :class="{ 'lead__detail--wide': detail.multiline }"
          >
            <app-textarea v-if="detail.multiline" :value="detail.value" readonly rows="2" class="lead__travel-dates" />
            <app-input v-else :value="detail.value" readonly />
          </app-form-group>
        </div>
      </div>
    </div>
    <template v-if="leadInfo.manager">
      <app-cells><h2 class="title">Make a story itineraries</h2></app-cells>
      <app-form-group v-if="is_general_manager || (!leadInfo.analytic_info?.confirmation_date && (is_sales_manager || is_sales_supervisor || is_support))">
        <app-input
          v-model="itinerary_link"
          @keyup.native.enter="onItineraryPost"
          :error="$v.itinerary_link.$error"
          class="input--300"
          placeholder="Enter the link and press enter"
        />
        <template #error>
          <div v-if="$v.itinerary_link.$invalid">Wrong link</div>
        </template>
      </app-form-group>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default table-default--indent"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #count="{ index }">{{ index }}</template>
        <template #itinerary_link="{ row }">
          <div class="table-default__controls">
            <a
              :href="`https://story.altezza.travel/clients/${row.itinerary_id}`"
              target="_blank"
              class="table-default__activity table-default__activity--itinerary"
              :class="{ 'table-default__activity--active': row.itinerary_id }"
            />
            <a
              :href="`https://story.altezza.travel/itinerary/${row.hash}`"
              target="_blank"
              class="table-default__activity table-default__activity--clients"
              :class="{ 'table-default__activity--active': row.hash }"
            />
          </div>
        </template>
        <template #itinerary_amount="{ row }">
          <span
            v-tooltip="{ content: `${row.amount ? `${row.amount.toLocaleString()} USD` : ''}`, placement: 'top-start', offset: 10 }">
            {{ row.itinerary_amount ? row.itinerary_amount.toLocaleString() : '-' }} {{ row.itinerary_currency?.code }}
          </span>
        </template>
        <template #commission="{ row }">
          {{ row.commission ? `${row.commission.toLocaleString()} USD` : '-' }}
        </template>
        <template #confirmed="{ row }">
          <button
            v-if="is_general_manager || (!leadInfo.analytic_info?.confirmation_date && (is_sales_manager || is_sales_supervisor))"
            @click="onPatchItineraryChange(row, {confirmed: !row.confirmed})"
            class="status-tag"
            :style="{ 'backgroundColor': row.confirmed ? '#77b017' : '#5f5f5f' }"
          >
            {{ row.confirmed ? 'Confirmed' : 'Proposal' }}
          </button>
          <span
            v-else
            class="status-tag"
            :style="{ 'backgroundColor': row.confirmed ? '#77b017' : '#5f5f5f' }"
          >
            {{ row.confirmed ? 'Confirmed' : 'Proposal' }}
          </span>
        </template>
        <template #created_at="{ row }">
          {{ dateTimeFormat(row.created_at) }}
        </template>
        <template #updated_at="{ row }">
          {{ dateTimeFormat(row.updated_at) }}
        </template>
        <template #skip_hotels="{ row }">
          <label
            class="checkbox checkbox--table"
          >
            <input
              @change="onPatchItineraryChange(row, {skip_hotels: row.skip_hotels})"
              v-model="row.skip_hotels"
              type="checkbox"
              name="checked_all"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowDeleteModal(row.id, 'itinerary')" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <template v-if="table_communication.items.length">
        <app-cells><h2 class="title">Communication history</h2></app-cells>
        <v-client-table
          :data="table_communication.items"
          :columns="table_communication.columns"
          :options="table_communication.options"
          class="table-default table-default--indent"
        >
          <template #h__report><div class="table-default__left">Report</div></template>
          <template #date="{ row }">
            {{ dateTimeFormat(row.date) }}
          </template>
          <template #methods="{ row }">
            {{ methodsFormat(row.methods) }}
          </template>
          <template #report="{ row }"><div class="table-default__left">{{ row.report || '-' }}</div></template>
          <template #created_by="{ row }">
            <div class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
                  v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
              {{ dateTimeFormatFull(row.created_at) }}
            </div>
          </template>
          <template #controls="{ row }">
            <div class="table-default__controls">
              <button @click="onShowDeleteModal(row.id, 'communication')" type="button">
                <img src="@/assets/img/delete-table-icon.svg" alt="">
              </button>
            </div>
          </template>
        </v-client-table>
      </template>
      <template v-if="table_follow.items.length">
        <app-cells><h2 class="title">Follow up history</h2></app-cells>
        <v-client-table
          :data="table_follow.items"
          :columns="table_follow.columns"
          :options="table_follow.options"
          class="table-default table-default--indent"
        >
          <template #h__template>
            <div class="table-default__left">Template</div>
          </template>
          <template #count="{ row, index }">{{ index }}</template>
          <template #created_by="{ row }">
            <div class="table-default__controls">
              <div class="table-default__photo">
                <img
                  :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
                  v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }"
                >
              </div>
              {{ dateTimeFormatFull(row.created_at) }}
            </div>
          </template>
          <template #template="{ row }">
            <div class="table-default__left">{{ row.template.template.name }}</div>
          </template>
          <template #delivered="{ row }">
            <div class="table-default__controls">
                        <span v-if="!row.sendpulse_log?.delivered" class="status-tag"
                              :style="{backgroundColor: '#e85c20'}">No</span>
              <span v-else>{{ dateTimeFormatFull(row.sendpulse_log?.delivered_at) }}</span>
            </div>
          </template>
          <template #opened="{ row }">
            <div class="table-default__controls">
              <span v-if="!row.sendpulse_log?.opened" class="status-tag" :style="{backgroundColor: '#e85c20'}">No</span>
              <span v-else>{{ dateTimeFormatFull(row.sendpulse_log?.opened_at) }}</span>
            </div>
          </template>
          <template #click_logs="{ row }">{{ row.sendpulse_log?.click_logs.length }}</template>
        </v-client-table>
      </template>
    </template>
    <template v-if="table_comment.items.length">
      <app-cells><h2 class="title">Comments</h2></app-cells>
      <v-client-table
        :data="table_comment.items"
        :columns="table_comment.columns"
        :options="table_comment.options"
        class="table-default table-default--indent"
      >
        <template #h__created><div class="table-default__left">Created by</div></template>
        <template #h__comment><div class="table-default__left">Comment</div></template>
        <template #created="{ row }">
          <div v-if="row.created_by" class="table-default__controls table-default__left">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
                v-tooltip="{ content: row.created_by.full_name, placement: 'top-end', offset: 10 }"
              >
            </div>
            {{ formatDateTime(row.created_at) }}
          </div>
        </template>
        <template #comment="{ row }"><div class="table-default__left">{{ row.comment }}</div></template>
      </v-client-table>
    </template>
    <template v-if="leadInfo.quick_payment">
      <app-cells><h2 class="title">Join deposit payment</h2></app-cells>
      <v-client-table
        :data="[leadInfo.quick_payment]"
        :columns="table_quick.columns"
        :options="table_quick.options"
        class="table-default table-default--indent"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #h__status><div class="table-default__left">Status</div></template>
        <template #created_at="{ row }">{{ dateTimeFormatFull(row.created_at) }}</template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #amount_gross="{ row }">{{ row.amount_gross.toLocaleString() }} {{ row.currency_symbol }}</template>
        <template #amount_commission="{ row }">{{ row.amount_commission.toLocaleString() }} {{ row.currency_symbol }}</template>
        <template #amount_received="{ row }">{{ row.amount_received.toLocaleString() }} {{ row.currency_symbol }}</template>
        <template #payment_method="{ row }">{{ capitalizeString(row.payment_method) }}</template>
        <template #status="{ row }">
          <div class="table-default__left">
            <span
              class="status-tag"
              :style="{ 'backgroundColor': row.paid ? '#77b017' : '#e85c20' }"
            >
              {{ row.paid ? 'Paid' : 'Unpaid' }}
            </span>
          </div>
        </template>
        <template #payment="{ row }">{{ row.paid ? dateTimeFormatFull(row.created_at) : '-' }}</template>
      </v-client-table>
    </template>
    <LeadManagementGuidance
      :lead-id="leadInfo.id"
      :initial="leadInfo.management_guidance"
      design="legacy"
      class="lead__management-guidance"
    />
    <div class="lead__wrapper lead__website-request" :class="{'lead__wrapper--indent': leadInfo.direct_request_info?.request}" v-if="leadInfo.request_info">
      <app-cells><h2 class="title">Website request</h2></app-cells>
      <app-cells v-if="leadInfo.request_info.url">
        URL:
        <a :href="leadInfo.request_info.url" class="lead__link" target="_blank">{{ leadInfo.request_info.url }}</a>
      </app-cells>
      <div v-if="leadInfo.request_info?.preferred_communication_method || leadInfo.request_info?.currency" class="lead__row">
        <app-form-group v-if="leadInfo.request_info.preferred_communication_method" label="Communication">
          <app-input v-model="leadInfo.request_info.preferred_communication_method" readonly />
        </app-form-group>
        <app-form-group v-if="leadInfo.request_info?.currency" label="Currency">
          <app-input v-model="leadInfo.request_info.currency.code" readonly />
        </app-form-group>
      </div>
      <app-form-group v-if="leadInfo.request_info.request">
        <app-textarea v-model="leadInfo.request_info.request" readonly class="textarea--mh-250"/>
      </app-form-group>
      <div class="lead__row">
        <app-form-group label="IP">
          <app-input v-model="leadInfo.request_info.ip_address" readonly/>
        </app-form-group>
        <app-form-group label="City">
          <app-input v-model="leadInfo.request_info.city" readonly/>
        </app-form-group>
        <app-form-group label="Region">
          <app-input v-model="leadInfo.request_info.region" readonly/>
        </app-form-group>
      </div>
      <div class="lead__row">
        <app-form-group label="Local time" class="form-group--no-indent">
          <app-input v-model="leadInfo.request_info.time" readonly/>
        </app-form-group>
        <app-form-group label="Browser" class="form-group--no-indent">
          <app-input v-model="leadInfo.request_info.browser" readonly/>
        </app-form-group>
        <app-form-group label="Invalid phone" class="form-group--no-indent">
          <app-input v-model="leadInfo.phone_invalid" readonly/>
        </app-form-group>
      </div>
    </div>
    <div class="lead__wrapper" v-if="leadInfo.direct_request_info?.request">
      <app-cells><h2 class="title">Direct request</h2></app-cells>
      <app-textarea v-model="leadInfo.direct_request_info.request" readonly class="textarea--mh-250"/>
    </div>
    <template v-if="leadInfo.double_request.length">
      <app-cells><h2 class="title">Double requests</h2></app-cells>
      <v-client-table
        :data="leadInfo.double_request"
        :columns="table_double.columns"
        :options="table_double.options"
        class="table-default"
      >
        <template #h__status.name>
          <div class="table-default__left">Status</div>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #status.name="{ row }">
          <div class="table-default__left">
            <span class="status-tag" :style="{ 'backgroundColor': row.status.color }">{{ row.status.name }}</span>
          </div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left table-default__controls">
            <span
              v-tooltip="{ content: row.note, placement: 'top-start', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.note}"
            ></span>
            <span
              :class="`fflag ff-md fflag-${row.country ? row.country.code : 'empty'}`"
              v-tooltip="{ content: row.country ? row.country.name : 'Not provided', placement: 'top-start', offset: 10 }"
            ></span>
            <router-link :to="{name: 'leads-detail-lead', params: {id: row.id }}"
                         class="table-default__link-border">
              {{ row.name }}
            </router-link>
            <span v-if="row.pax">({{ row.pax }})</span>
            <span v-if="row.tour_operator"
                  v-tooltip="{ content: row.tour_operator.name, placement: 'top-start', offset: 10 }">
              ({{ row.tour_operator.short_name }})
            </span>
          </div>
        </template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.email)"
              class="table-default__activity table-default__activity--mail"
              :class="{'table-default__activity--active': row.email}"
            />
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.phone}"
            />
            <a
              :href="`https://t.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--tg"
              :class="{'table-default__activity--active': row.telegram}"
            />
            <a
              :href="`https://wa.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.whatsapp}"
            />
            <a
              :href="row.facebook"
              target="_blank"
              class="table-default__activity table-default__activity--facebook"
              :class="{'table-default__activity--active': row.facebook}"
            />
            <a
              :href="row.instagram"
              target="_blank"
              class="table-default__activity table-default__activity--instagram"
              :class="{'table-default__activity--active': row.instagram}"
            />
          </div>
        </template>
        <template #last_contact="{ row }">
          <div class="table-default__controls">
                      <span v-if="row.last_contact">
                        <span v-if="row.last_contact_expired" class="status-tag"
                              :style="{ 'backgroundColor': '#e85c20' }">
                          {{ dateTimeFormat(row.last_contact) }}
                        </span>
                        <span v-else>{{ dateTimeFormat(row.last_contact) }}</span>
                      </span>
            <span v-else class="status-tag" :style="{ 'backgroundColor': '#f0c427' }">
                        No contact
                      </span>
          </div>
        </template>
        <template #activities="{ row }">
          <div class="table-default__controls">
            <span
              class="table-default__activity table-default__activity--climbing table-default__activity--no-events"
              :class="{'table-default__activity--active': row.climbing}"
            />
            <span
              class="table-default__activity table-default__activity--safari table-default__activity--no-events"
              :class="{'table-default__activity--active': row.safari}"
            />
            <span
              class="table-default__activity table-default__activity--day_trips table-default__activity--no-events"
              :class="{'table-default__activity--active': row.day_trip}"
            />
            <span
              class="table-default__activity table-default__activity--island table-default__activity--no-events"
              :class="{'table-default__activity--active': row.island}"
            />
          </div>
        </template>
        <template #travel_time="{ row }">
          {{ dateFormatMonth(row.travel_time) }}
        </template>
        <template #itinerary="{ row }">
          <span
            v-tooltip="{ content: `${row.itinerary?.amount ? `${row.itinerary.amount.toLocaleString()} USD` : ''}`, placement: 'top-start', offset: 10 }">
            {{
              row.itinerary?.itinerary_amount ? row.itinerary.itinerary_amount.toLocaleString() : '-'
            }} {{ row.itinerary?.itinerary_currency?.code }}
          </span>
        </template>
        <template #commission="{ row }">
          {{ row.itinerary?.commission ? `${row.itinerary?.commission.toLocaleString()} USD` : '-' }}
        </template>
        <template #manager="{ row }">
          <div class="table-default__controls">
            <div v-if="row.manager" class="table-default__photo">
              <img
                :src="getUserAvatar(row.manager.crop.crop_url)" alt="Avatar"
                v-tooltip="{ content: row.manager.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
            <span v-else>-</span>
          </div>
        </template>
      </v-client-table>
    </template>
    <modal
      name="delete"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="Warning">
          Delete {{ delete_modal_type = 'communication' ? 'communication' : 'link' }}
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteItem" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="date-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('date-modal')" class="popup__close" type="button"></button>
        <div class="popup__title"><span>Report communication</span></div>
        <div class="popup__body">
          <app-form
            :form.sync="date_form"
            :form-config="date_form_config"
            @submit="sendDateForm"
            button-submit-text="Save"
            ref="date_form"
          />
        </div>
      </div>
    </modal>
    <modal
      name="follow-modal"
      :width="450"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('follow-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Choose follow up to send</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFollowForm">
            <app-form-group>
              <app-cells :indent="false">
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.climbing"
                              label="Climbing"/>
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.safari"
                              label="Safari"/>
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.day_trip"
                              label="Day trip"/>
                <app-checkbox @change="onChangeFilterFollow" v-model="follow_filters.island"
                              label="Island"/>
              </app-cells>
            </app-form-group>
            <app-form-group label="Template" required>
              <app-select
                v-model="follow_form.template"
                :options="templates_options"
                :reduce="item => item.id"
                :clearable="false"
                :get-option-label="item => item.template.name"
                :display-field="option => option.template.name"
                placeholder="Template"
                :error="$v.follow_form.template.$error"
              />
              <template #error>
                <div v-if="$v.follow_form.template.$dirty && !$v.follow_form.template.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="follow_submit">Send</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="client-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('client-form')" class="popup__close" type="button"></button>
        <div class="popup__title">Create group</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckFormCreateGroup">
            <app-form-group label="Name" label-for="name" required>
              <app-input
                v-model="form_create_group.name"
                :error="$v.form_create_group.name.$error"
                id="name"
                placeholder="Name"
                name="group_name"
              />
              <template #error>
                <div v-if="$v.form_create_group.name.$dirty && !$v.form_create_group.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form_create_group.$error" type="submit" ref="submit">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="comment-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('comment-form')" class="popup__close" type="button"></button>
        <div class="popup__title">Add comment</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckCommentForm">
            <app-form-group label="Notes" label-for="notes" required>
              <app-textarea
                v-model="comment_form.comment"
                id="notes"
                placeholder="Notes"
                :error="$v.comment_form.comment.$error"
              />
              <template #error>
                <div v-if="$v.comment_form.comment.$dirty && !$v.comment_form.comment.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.comment_form.$error" type="submit" ref="comment_submit">Add</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteLeadCommunication,
  deleteLeadsItineraries,
  getClientsTourOperators,
  getCountries,
  getCountryTimezones,
  getLanguages,
  getLeadComments,
  getLeadCommunications,
  getLeadFollowUps,
  getLeadFollowUpTemplates,
  getLeadsCommunicationMethods,
  getLeadsItineraries,
  getLeadsSources,
  getLeadsTrengoTickets,
  patchLead,
  patchLeadsItineraries,
  postClientsGroup,
  postLeadComment,
  postLeadCommunication,
  postLeadFollowUp,
  postLeadsItinerary,
} from '@/http'
import { format, formatISO, parse, parseISO } from 'date-fns'
import { email, required, requiredIf, url } from 'vuelidate/lib/validators'
import AppCheckboxPic from '@/components/AppCheckboxPic'
import LeadManagementGuidance from '@/views/crm/task-tracker/LeadManagementGuidance.vue'
import { formatTravelDates } from '@/views/crm/task-tracker/leadFlowDate'
import capitalize from 'lodash/capitalize'
import debounce from 'lodash/debounce'

export default {
  name: 'LeadsDetailLead',
  components: {AppCheckboxPic, LeadManagementGuidance},
  props: {
    leadInfo: Object,
  },
  data() {
    return {
      table_preloader: false,
      table_comment_preloader: false,
      itinerary_link: '',
      source_options: [],
      trengo_ticket_options: [],
      country_options: [],
      timezone_options: [],
      tour_operators_options: [],
      languages_options: [],
      form: {},
      table: {
        items: [],
        columns: ['count', 'itinerary_link', 'itinerary_amount', 'commission', 'confirmed', 'created_at', 'updated_at'],
        options: {
          headings: {
            count: '#',
            itinerary_link: 'Links',
            itinerary_amount: 'Amount',
            commission: 'Commission',
            confirmed: 'Status',
            created_at: 'Created',
            updated_at: 'Last sync',
            skip_hotels: 'Skip hotels',
            controls: '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_communication: {
        items: [],
        columns: ['date', 'methods', 'report', 'created_by'],
        options: {
          headings: {
            date: 'Date',
            methods: 'Methods',
            report: 'Report',
            created_by: 'Created by',
            controls: '',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_follow: {
        items: [],
        columns: ['count', 'created_by', 'template', 'delivered', 'opened', 'click_logs'],
        options: {
          headings: {
            count: '#',
            created_by: 'Sent by',
            template: 'Template',
            delivered: 'Delivered',
            opened: 'Opened',
            click_logs: 'Clicks',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_double: {
        items: [],
        columns: [
          'status.name',
          'name',
          'communication',
          'last_contact',
          'activities',
          'travel_time',
          'itinerary',
          'commission',
          'manager'
        ],
        options: {
          headings: {
            'status.name': 'Status',
            'name': 'Name',
            'communication': 'Communication',
            'last_contact': 'Last contact',
            'activities': 'Activities',
            'travel_time': 'Travel time',
            'manager': 'Manager',
            'itinerary': 'Amount',
            'commission': 'Commission',
          },
          pagination: {show: false},
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_quick: {
        items: [],
        columns: ['created_at', 'name', 'amount_gross', 'amount_commission', 'amount_received', 'payment_method', 'status', 'payment'],
        options: {
          headings: {
            created_at: 'Created',
            name: 'Name',
            amount_gross: 'Deducted',
            amount_commission: 'Commission',
            amount_received: 'Received',
            payment_method: 'Method',
            status: 'Status',
            payment: 'Payment',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      item_to_delete: null,
      delete_modal_type: '',
      date_form: {
        methods: [],
        date: formatISO(new Date()),
        report: null,
      },
      date_form_config: [
        {
          component: 'app-date-picker',
          model: 'date',
          label: 'Date',
          attrs: {
            format: 'DD MMM YYYY HH:mm',
            valueType: 'YYYY-MM-DDTHH:mm:ssZ',
            placeholder: 'Date',
            popupClass: 'datepicker__popup',
            titleFormat: 'DD MMM YYYY HH:mm',
            timeTitleFormat: 'DD MMM YYYY',
            type: 'datetime',
            clearable: false
          },
          validations: { required },
          grid: {
            row: 1,
            col: 1,
            width: 1
          }
        },
        {
          component: 'app-select',
          model: 'methods',
          label: 'Communication methods',
          attrs: {
            options: [],
            clearable: false,
            label: 'name',
            placeholder: 'Communication methods',
            multiple: true,
            class: 'select--multiple'
          },
          validations: { required },
          grid: {
            row: 2,
            col: 1,
            width: 1
          },
        },
        {
          component: 'app-textarea',
          model: 'report',
          label: 'Report',
          attrs: {
            id: 'report',
            rows: '5',
            placeholder: 'Write a report'
          },
          validations: {
            required: requiredIf(() => {
              return this.report_required
            })
          },
          grid: {
            row: 3,
            col: 1,
            width: 1
          },
        }
      ],
      form_create_group: {},
      follow_form: {
        template: null
      },
      templates_options: [],
      follow_filters: {},
      comment_form: {},
      table_comment: {
        items: [],
        columns: ['created', 'comment'],
        options: {
          headings: {
            created: 'Created by',
            comment: 'Comment',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 30,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  computed: {
    otherDetails() {
      return [
        { label: 'Travel planning period', value: this.leadInfo.travel_planning_period?.name },
        { label: 'Adults', value: this.leadInfo.adults },
        { label: 'Teenagers', value: this.leadInfo.teenagers },
        { label: 'Children', value: this.leadInfo.children },
        { label: 'Climb Type', value: this.leadInfo.climb_type },
        { label: 'Group Type', value: this.leadInfo.group_type },
        { label: 'Accomodation Level', value: this.leadInfo.hotel_stars },
        { label: 'Safari Duration', value: this.leadInfo.safari_duration },
        { label: 'Kilimanjaro Duration', value: this.leadInfo.kilimanjaro_duration },
        { label: 'Beach Duration', value: this.leadInfo.beach_duration },
        { label: 'Zanzibar Duration', value: this.leadInfo.other_duration },
        { label: 'Budget per person', value: this.leadInfo.trip_price },
        { label: 'Travel time', value: this.leadInfo.travel_time ? this.dateFormatMonth(this.leadInfo.travel_time) : null },
        { label: 'Kids ages', value: this.leadInfo.request_info?.child_ages?.trim() },
        { label: 'Travel Dates', value: formatTravelDates(this.leadInfo.travel_dates), multiline: true },
      ].filter(detail => detail.value !== null && detail.value !== undefined && detail.value !== '')
    },
    report_required() {
      if (!this.date_form?.methods?.length) return false
      return this.date_form.methods.some(item => item.report_required)
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_support() {
      return this.$store.state.userGroups.map(item => item.code).includes('support')
    },
    timezoneSourceLabel() {
      if (this.leadInfo.country_timezone_estimated) return 'Country default (estimated)'
      return this.sourceLabel(this.leadInfo.country_timezone_source)
    },
  },
  validations: {
    form: {
      source: {required},
      name: {required},
      language: {required},
      email: {email},
      phone: {},
      facebook: {url},
      instagram: {url},
      asana: {url},
      next_contact: {
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
    },
    itinerary_link: {url},
    follow_form: {
      template: {required},
    },
    form_create_group: {
      name: { required },
    },
    comment_form: {
      comment: { required },
    },
  },
  watch: {
    leadInfo: {
      immediate: true,
      handler(val) {
        this.form = {
          asana: val.asana,
          climbing: val.climbing,
          island: val.island,
          safari: val.safari,
          day_trip: val.day_trip,
          name: val.name,
          first_name: val.first_name,
          last_name: val.last_name,
          pax: val.pax,
          email: val.email,
          phone: val.phone,
          whatsapp: val.whatsapp,
          telegram: val.telegram,
          facebook: val.facebook,
          instagram: val.instagram,
          note: val.note,
          source: val.source.code,
          travel_time: val.travel_time,
          tour_operator: val.tour_operator?.id || null,
          country: val.country?.code || null,
          country_timezone: val.country_timezone?.id || null,
          language: val.language?.code || null,
          next_contact: val.next_contact,
          trengo_ticket: val.trengo_ticket,
        }
      }
    }
  },
  created() {
    if (this.is_sales_supervisor) this.table.columns.push('skip_hotels')
    if (this.is_general_manager) {
      this.table_communication.columns.push('controls')
      this.table.columns.push('skip_hotels')
    }
    if (this.is_general_manager || (!this.leadInfo.analytic_info?.confirmation_date && (this.is_sales_manager || this.is_sales_supervisor || this.is_support))) {
      this.table.columns.push('controls')
    }
    this.getItineraries()
    this.fetchCommunications()
    this.fetchFollowUps()
    this.fetchItemsComment()
    getCountries().then(response => {
      this.country_options = response.data
    })
    if (this.leadInfo.country?.code) this.loadTimezones(this.leadInfo.country.code)
    getClientsTourOperators().then(res => {
      this.tour_operators_options = res.data
    })
    getLeadsSources().then(res => {
      this.source_options = res.data
    })
    getLanguages().then(res => {
      this.languages_options = res.data
    })
    getLeadsCommunicationMethods().then(res => {
      this.date_form_config.find(item => item.model === 'methods').attrs.options = res.data
    })
  },
  methods: {
    onOpenModalComment() {
      this.comment_form = {}
      this.$v.comment_form.$reset()
      this.$modal.show('comment-form')
    },
    onCheckCommentForm() {
      this.$v.comment_form.$touch()
      if (this.$v.comment_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormComment()
      }
    },
    sendFormComment() {
      this.$refs.comment_submit.preload = true
      postLeadComment(this.$route.params.id, this.comment_form)
        .finally(() => { this.$refs.comment_submit.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Comment added',
          })
          this.fetchItemsComment()
          this.$modal.hide('comment-form')
        })
    },
    fetchItemsComment() {
      getLeadComments(this.$route.params.id)
        .then(response => {
          this.table_comment.items = response.data
        })
    },
    onCheckFormCreateGroup() {
      this.$v.form_create_group.$touch()
      if (this.$v.form_create_group.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFormCreateGroup()
      }
    },
    sendFormCreateGroup() {
      this.$refs.submit.preload = true
      this.form_create_group.lead = +this.$route.params.id
      postClientsGroup(this.form_create_group)
        .finally(() => { this.$refs.submit.preload = false })
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Group created',
          })
          this.$router.push({ name: 'clients-groups-general', params: { id: response.data.id } })
        })
    },
    fetchFollowUpTemplates() {
      getLeadFollowUpTemplates({
        language: this.leadInfo.language.code,
        available_statuses: [this.leadInfo.status.code],
        climbing: this.follow_filters.climbing || '',
        day_trip: this.follow_filters.day_trip || '',
        island: this.follow_filters.island || '',
        safari: this.follow_filters.safari || '',
      }).then(response => {
        this.templates_options = response.data
      })
    },
    fetchFollowUps() {
      getLeadFollowUps(this.$route.params.id)
        .then(response => {
          this.table_follow.items = response.data
        })
    },
    fetchCommunications() {
      getLeadCommunications(this.$route.params.id)
        .then(response => {
          this.table_communication.items = response.data
        })
    },
    onShowFollowModal() {
      this.follow_filters = {
        climbing: this.leadInfo.climbing,
        day_trip: this.leadInfo.day_trip,
        island: this.leadInfo.island,
        safari: this.leadInfo.safari,
      }
      this.fetchFollowUpTemplates()
      this.follow_form.template = null
      this.$v.follow_form.$reset()
      this.$modal.show('follow-modal')
    },
    onChangeFilterFollow() {
      this.fetchFollowUpTemplates()
    },
    onCheckFollowForm() {
      this.$v.follow_form.$touch()
      if (this.$v.follow_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendFollowForm()
      }
    },
    sendFollowForm() {
      this.$refs.follow_submit.preload = true
      postLeadFollowUp(this.$route.params.id, {template: this.follow_form.template})
        .then(() => {
          this.$emit('update-info')
          this.fetchFollowUps()
          this.$notify({
            type: 'success',
            title: 'Follow up created',
          })
          this.$modal.hide('follow-modal')
        })
        .catch(() => {
          this.$refs.follow_submit.preload = false
        })
    },
    onShowDateModal() {
      this.date_form.date = formatISO(new Date())
      this.date_form.report = null
      this.date_form.methods = []
      this.$modal.show('date-modal')
    },
    sendDateForm() {
      this.$refs.date_form.$refs.submit_button.preload = true
      const data = {...this.date_form}
      data.methods = data.methods.map(item => item.code)
      postLeadCommunication(this.$route.params.id, data)
        .then(() => {
          this.$emit('update-info')
          this.fetchCommunications()
          this.$notify({
            type: 'success',
            title: 'Communication created',
          })
          this.$modal.hide('date-modal')
        })
        .catch(() => {
          this.$refs.date_form.$refs.submit_button.preload = false
        })
    },
    getItineraries() {
      getLeadsItineraries(this.$route.params.id)
        .then(response => {
          this.table.items = response.data
          this.table_preloader = false
        })
    },
    onPatchItineraryChange(row, data) {
      this.table_preloader = true
      patchLeadsItineraries(this.$route.params.id, row.id, data)
        .then(() => {
          this.getItineraries()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
        })
    },
    capitalizeString(str) {
      return capitalize(str)
    },
    onItineraryPost() {
      if (this.$v.itinerary_link.$invalid || !this.itinerary_link) return
      this.table_preloader = true
      postLeadsItinerary(this.$route.params.id, {itinerary_link: this.itinerary_link})
        .then(() => {
          this.getItineraries()
          this.$notify({
            type: 'success',
            title: 'Link added',
          })
          this.itinerary_link = ''
          this.$v.itinerary_link.$reset()
        })
    },
    onCheckForm(data) {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        patchLead(this.$route.params.id, data)
          .then(() => {
            this.$emit('update-info')
            this.$notify({
              type: 'success',
              title: 'Data saved',
            })
            this.$v.form.$reset()
          })
          .catch(() => { this.$emit('update-info') })
      }
    },
    sourceLabel(source) {
      const labels = {
        website: 'Website',
        ipstack: 'IPStack',
        phone_number: 'Phone',
        country_default: 'Country default',
        manual: 'Manual',
        legacy: 'Legacy',
      }
      return labels[source] || 'Unknown'
    },
    timezoneLabel(timezone) {
      return timezone.label || timezone.timezone_name
    },
    loadTimezones(country) {
      return getCountryTimezones(country).then(response => {
        if (this.form.country === country) this.timezone_options = response.data
      })
    },
    onCountryChange() {
      this.form.country_timezone = null
      this.timezone_options = []
      const country = this.form.country
      this.onCheckForm({ country })
      if (country) this.loadTimezones(country)
    },
    onTimezoneChange() {
      this.onCheckForm({ country_timezone: this.form.country_timezone })
    },
    onShowDeleteModal(id, type) {
      this.item_to_delete = id
      if (type === 'communication') this.delete_modal_type = 'communication'
      else this.delete_modal_type = 'itinerary'
      this.$modal.show('delete')
    },
    onDeleteItem() {
      this.$refs.delete.preload = true
      if (this.delete_modal_type === 'communication') {
        deleteLeadCommunication(this.$route.params.id, this.item_to_delete)
          .then(() => {
            this.fetchCommunications()
            this.$notify({
              type: 'success',
              title: 'Communication deleted',
            })
            this.$modal.hide('delete')
          })
      } else {
        deleteLeadsItineraries(this.$route.params.id, this.item_to_delete)
          .then(() => {
            this.getItineraries()
            this.$notify({
              type: 'success',
              title: 'Link deleted',
            })
            this.$modal.hide('delete')
          })
      }
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    dateTimeFormatFull(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    methodsFormat(methods) {
      return methods.map(item => item.name).join(', ')
    },
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => {
          console.error('Something went wrong')
        })
    },
    dateFormatMonth(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'MMM yyyy') : '-'
    },
    formatDateTime(date) {
      if (!date) return ''
      return format(parseISO(date), 'dd MMMM yyyy HH:mm')
    },
    onTicketSearch(search, loading) {
      loading(true)
      this.searchTicket(loading, search, this)
    },
    searchTicket: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.trengo_ticket_options = []
      } else {
        getLeadsTrengoTickets({ search }).then(response => {
          vm.trengo_ticket_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>

<style lang="sass" scoped>
.lead__management-guidance
  box-sizing: border-box
  width: 50%
  min-width: 540px
  max-width: 960px
  margin-bottom: 20px

  +max-w(900)
    width: 100%
    min-width: 0
.lead__website-request
  margin-bottom: 20px
.lead__wrapper
  max-width: 1200px

  +max-w($laptop_md)
    max-width: 100%

.lead__wrapper--indent
  margin-bottom: 30px

.lead__name
  display: flex
  align-items: center
  gap: 10px
  margin-bottom: 25px
  color: #181818
  font-weight: 500

.lead__part
  margin-bottom: 20px

.lead__row
  display: grid
  gap: 10px 20px
  grid-template-columns: repeat(5, 1fr)

  +max-w($laptop_md)
    grid-template-columns: repeat(3, 1fr)

.lead__row--names
  grid-template-columns: repeat(3, minmax(0, 1fr))

.lead__row--demographics
  grid-template-columns: repeat(4, minmax(0, 1fr))

  +max-w($laptop_md)
    grid-template-columns: repeat(2, minmax(0, 1fr))

.lead__link
  display: inline-flex
  flex-direction: column
  width: max-content
  color: $color-text-base
  transition: color 0.3s

  &:after
    content: ''
    display: block
    height: 2px
    border-radius: 3px
    background-color: #aae182
    transform: translateY(-1px)
    transition: opacity 0.3s

  &:hover
    &:after
      opacity: 0

.lead__detail--wide
  grid-column: 1 / -1

.lead__travel-dates
  min-height: 60px

.lead__source
  color: $color-text-second
  font-size: 12px

.slide-small-enter-active
  transition-duration: 0.3s
  transition-timing-function: ease-in

.slide-small-leave-active
  transition-duration: 0.3s
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1)

.slide-small-enter-to, .slide-small-leave
  max-height: 147px
  overflow: hidden

.slide-small-enter, .slide-small-leave-to
  overflow: hidden
  max-height: 0
</style>
